/**
 * Pure mutation functions for document data actions.
 */

import type {IDocumentData} from "@buildwithflux/models";
import {difference, intersection, isEqual, isPlainObject} from "lodash";

/**
 * Pure mutation: applies a two-level-deep diff from snapshot data onto the document draft.
 * This optimizes patch size similar to how autosave diffing works.
 */
export function applySetDocumentDataFromSnapshot(draftState: IDocumentData, documentData: IDocumentData): void {
    // Simple, two-level-deep diff to optimize patch size,
    // similar to how autosave diffing works.
    for (const [key, docValue] of Object.entries(documentData)) {
        const draftKey = key as keyof IDocumentData;

        // HACK: latestPatch should always be re-created by withHistoryUpdate,
        // otherwise we get an "Error generating PCB data"
        if (draftKey === "latestPatch" || key === "latestActionRecordUid") {
            continue;
        }

        // HACK: ignore these because they are causing Firestore security rules
        // permission errors in changing null to undefined for keys like
        // "organization_owner_uid"
        if (docValue === null && draftState[draftKey] === undefined) {
            continue;
        }

        if (isPlainObject(docValue)) {
            const docKeys: string[] = Object.keys(docValue);
            const draftKeys: string[] = Object.keys(draftState[draftKey] || {});

            // Initialize the property if it doesn't exist
            // This prevents crashes when restoring a snapshot with properties
            // that don't exist in the current document state
            if (!draftState[draftKey]) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (draftState as any)[draftKey] = {};
            }

            // Remove keys that exist in draft but not in doc
            for (const id of difference(draftKeys, docKeys)) {
                delete (draftState[draftKey] as {[key: string]: unknown})[id];
            }

            // Add keys that exist in doc but not in draft
            for (const id of difference(docKeys, draftKeys)) {
                (draftState[draftKey] as {[key: string]: unknown})[id] = (docValue as {[key: string]: unknown})[id];
            }

            // Update keys that exist in both but have different values
            for (const id of intersection(docKeys, draftKeys).filter(
                (key) =>
                    !isEqual(
                        (draftState[draftKey] as {[key: string]: unknown})[key],
                        (docValue as {[key: string]: unknown})[key],
                    ),
            )) {
                (draftState[draftKey] as {[key: string]: unknown})[id] = (docValue as {[key: string]: unknown})[id];
            }
        } else if (draftState[draftKey] !== docValue) {
            // For non-object values, just assign directly
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (draftState as any)[draftKey] = docValue;
        }
    }
}
